// CANONICAL COPY: installations\smart-td\content\agreementTables.js (ruling 321 slice 3c, task list section 735). src\data\agreementTables.js is its
// projection, written by scripts\sync-installation.js - EDIT THE CANONICAL. An edit made only to the projection is refused by the cycle.
// THE AGREEMENT TABLES - data only (section 735). The code over them (LOCATIONS_BY_RAILROAD, sharedAgreementFor) and the
// committeeData re-export are in src\data\agreementData.js, which re-exports every table here, so no importer changed.
// Central agreement data — includes work locations per railroad.
// Work locations use a two-level structure where applicable:
//   Level 1: Primary Terminal (Yardmaster Work Location)
//   Level 2: Sub-yards / Satellite Facilities (Event Location)
//
// ABOLISHED POSITIONS are included and displayed in the work location dropdown
// with a visual indicator. Claims may still be filed against abolished locations
// for events that occurred prior to the abolishment date.

// ── SUB-YARD MAP ─────────────────────────────────────────────────────────────
// Maps primary terminal → sub-yard/satellite facility array.
// Terminals NOT in this map have no sub-yards (event location = work location).
export const SUB_YARDS = {
  // CSX Region 24 — Albany/Buffalo
  'Buffalo (Frontier), NY': [
    'Hump',
    'West End',
    'Bailey Avenue',
  ],
  'Albany (Selkirk), NY': ['Hump', 'East End', 'Top End'],
  'Syracuse (DeWitt), NY': [
    'DeWitt Yard',
  ],
  // CSX Region 26 — Cleveland/Erie
  'Cleveland (Collinwood), OH': [
    'Collinwood',
    'Van Site',
  ],
  // GO-SCL Region 21 — Jacksonville
  'Jacksonville, FL (Moncrief)': [
    'Moncrief',
    'Baldwin',
    'Duval Ramp',
  ],
};

// ── ABOLISHED POSITION REGISTRY ──────────────────────────────────────────────
// Source: GC Bob Keeley personal correspondence records, GO-342 tenure (~16 years)
// Abolished locations remain in dropdowns for historical claim filing purposes.
// Each entry records: abolishment date, work transferred to, and source document title.

// === LOCAL COMMITTEE - DERIVED, NOT STORED HERE ===
// LOCAL_COMMITTEE_LOOKUP was REMOVED 2026-08-17 (SS XI: a fact living in two places is a
// defect). It keyed the local committee on work location ALONE -- which cannot be correct,
// because the same location name occurs under more than one craft/railroad -- and it
// disagreed with the canonical territory map on 7 locations (6 CSX Northern Lines NJ/NY
// Metro locations 1949 vs 1951; Columbus Shop 1951 vs 1962), all of them Bob's own rulings
// of 2026-08-03 and 2026-08-04 recorded verified=Yes in that map.
// Canonical source: data/YCP_Location_Territory_Map.csv -> src/data/scopeKeyData.js.
// Derive it: resolveLocalCommittee(craft, railroad, workLocation) from src/data/routing.js.

export const ABOLISHED_POSITIONS = {
  // Region 26/27 — Collinwood Hub Network
  'Van Site, OH': {
    abolishedDate: 'March 14, 2021',
    workTransferredTo: 'Collinwood Yardmaster Desk',
    sourceDocument: 'Carrier Notice of Electronic Clerical & Coordination Consolidation — Van Site Facility',
    region: '26',
  },
  'Erie, PA': {
    abolishedDate: 'November 2, 2018',
    workTransferredTo: 'Collinwood Yardmaster Desk',
    sourceDocument: 'Addendum V-E: Operational Transfer of Buffalo/Erie Line Jurisdiction',
    region: '26',
  },
  'Cleveland/Parma, OH': {
    abolishedDate: 'August 19, 2022',
    workTransferredTo: 'Collinwood Yardmaster Desk',
    sourceDocument: 'Consolidation Agreement — Cleveland Terminal District & Parma Local Yards',
    region: '26',
  },
  'Ashtabula, OH': {
    abolishedDate: 'June 3, 2019',
    workTransferredTo: 'Collinwood Yardmaster Desk',
    sourceDocument: 'Region 27 Supplemental Abolishment Order — Lakefront Marine & Rail Terminal',
    region: '27',
  },
  // Region 23 — South Kearny Hub Network
  'North Bergen, NJ': {
    abolishedDate: 'January 11, 2024',
    workTransferredTo: 'South Kearny Yardmaster Desk',
    sourceDocument: 'Article 12 Framework Resolution — North Bergen Consolidation Directive',
    region: '23',
  },
  'Manville, NJ': {
    abolishedDate: 'October 28, 2023',
    workTransferredTo: 'South Kearny Yardmaster Desk',
    sourceDocument: 'Multi-Carrier Multi-Facility Attrition Alignment — Manville Joint Operations',
    region: '23',
  },
  // Region 25 — New England
  'Boston (Beacon Park), MA': {
    abolishedDate: 'September 15, 2017',
    workTransferredTo: 'Worcester Facility Desk',
    sourceDocument: 'District 1 Line Reduction & Terminal Shift — Beacon Park Tricks',
    region: '25',
  },
  'Readville, MA': {
    abolishedDate: 'May 22, 2020',
    workTransferredTo: 'Framingham Terminal Desk',
    sourceDocument: 'Framingham Remote Expansion Accord — Readville Station Elimination',
    region: '25',
  },
};

// ── AGREEMENTS ───────────────────────────────────────────────────────────────
// ── AGREEMENT RULES LOOKUP ────────────────────────────────────────────────────
// Maps agreement name → array of violation type options (Article/Rule # — Title)
// Source: Official agreement documents in YCP project knowledge.
// Used to populate the Violation Type dropdown based on selected agreement.
const CSX_SYSTEM_2002_VIOLATIONS = [
    'Article 1 — Scope Violation',
    'Article 2 — Basic Day / Overtime / Starting Time / Call Violation',
    'Article 3 — Five-Day Work Week Violation',
    'Article 4 — Relief Assignment Violation',
    'Article 5 — Position Transfer Violation',
    'Article 6 — Rate of Pay Violation',
    'Article 7 — Classification Violation',
    'Article 8 — Payroll / Direct Deposit Violation',
    'Article 9 — Programming Violation',
    'Article 10 — Regional Seniority / Displacement / Bulletin Violation',
    'Article 11 — Displacement Rights Violation',
    'Article 12 — Reduction in Force Violation',
    'Article 13 — Failure to Return to Work — Improper Handling',
    'Article 14 — Bulletining New Positions Violation',
    'Article 15 — Hold-Down Violation',
    'Article 16 — Seniority Roster Error or Omission',
    'Article 17 — Change in Assignment / Rate Violation',
    'Article 18 — Personal Automobile Violation',
    'Article 19 — Leave of Absence Violation',
    'Article 20 — Claims or Grievances — Procedure Violation',
    'Article 21 — Discipline / Appeals — Improper Procedure',
    'Article 22 — Vacation Violation',
    'Article 23 — Holiday Violation',
    'Article 24 — Bereavement Leave Violation',
    'Article 25 — Personal Leave Day Violation',
    'Article 26 — Meal Period Violation',
    'Article 27 — Joint Medical Board Violation',
    'Article 28 — Jury Duty Violation',
    'Article 29 — Miscellaneous Provisions Violation',
    'Article 30 — General Provisions Violation',
    // Addenda: only Addendum H is verified of record (Chief Yardmaster, Bob-confirmed 2026-08-04).
    // Addenda G / I / J from prior data are UNVERIFIED pending the agreement's addenda section.
    'Addendum H — Chief Yardmaster Agreement Violation',
];

export const AGREEMENT_RULES = {

  // ── AMTRAK YARDMASTER AGREEMENT (eff. Jan 1, 1983; Labor Relations Printout, Nov 1, 2000) ──
  'Amtrak Yardmaster Agreement (eff. Jan 1, 1983; Labor Relations Printout, Nov 1, 2000)': [
    'Rule 1 — Scope Violation',
    'Rule 3 — Probationary Period Violation',
    'Rule 4 — Consideration for Promotion Denied',
    'Rule 5 — Seniority District Violation',
    'Rule 6 — Seniority Date Dispute',
    'Rule 7 — Roster Error or Omission',
    'Rule 8 — Right of Protest Denied',
    'Rule 9 — Official Position / Seniority Retention Violation',
    'Rule 10 — Exercise of Seniority Denied',
    'Rule 11 — Training for Greater Responsibility Denied',
    'Rule 12 — Leave of Absence Violation',
    'Rule 13 — Qualifications Dispute',
    'Rule 14 — Bulletin / Award Violation',
    'Rule 15 — Changes Requiring Re-Advertisement',
    'Rule 16 — Return from Absence — Improper Handling',
    'Rule 17 — Basis of Pay Violation',
    'Rule 18 — Overtime Violation',
    'Rule 19 — Calls Violation',
    'Rule 20 — Holiday Pay Violation',
    'Rule 21 — Rest Days Violation',
    'Rule 22 — Attending Court Violation',
    'Rule 23 — Jury Duty Violation',
    'Rule 28 — Programming Violation',
    'Rule 29 — Compensation / Claims Procedure Violation',
    'Rule 30 — Extra List Violation',
    'Rule 31 — Position Abolished — Improper Handling',
    'Rule 32 — Assignment Beginning Violation',
    'Rule 34 — Starting Time / Starting Point Violation',
    'Rule 35 — Private Auto Violation',
    'Rule 36 — Discipline / Appeals — Improper Procedure',
    'Rule 38 — Training Violation',
    'Rule 39 — Personal Leave Violation',
    'Rule 40 — Rate Progression Violation',
    'Rule 42 — Non-Discrimination Violation',
    'Rule 45 — Notice of Change Violation',
    'New Rule 1 — Productivity Council Violation',
    'New Rule 2 — Occupational Health Violation',
    'Article I (2000) — Wage / GWI Violation',
    'Article II (2000) — COLA Violation',
    'Article VII (2000) — Work Rule Change Violation',
  ],

  // ── CSX YARDMASTER SYSTEM AGREEMENT (eff. July 1, 2002) ──────────────────
  'CSX Yardmaster System Agreement (eff. July 1, 2002)': CSX_SYSTEM_2002_VIOLATIONS,

  // ── CSX — B&O/B&I/CSXI TERRITORY ────────────────────────────────────────
  'CSX Yardmaster System Agreement (eff. July 1, 2002) — B&O/B&I/CSXI Territory': CSX_SYSTEM_2002_VIOLATIONS,

  // ── CSX — C&O/LN/MONON TERRITORY ────────────────────────────────────────
  'CSX Yardmaster System Agreement (eff. July 1, 2002) — C&O/LN/Monon Territory': CSX_SYSTEM_2002_VIOLATIONS,

  // ── CSX — SCL TERRITORY ──────────────────────────────────────────────────
  'CSX Yardmaster System Agreement (eff. July 1, 2002) — SCL Territory': CSX_SYSTEM_2002_VIOLATIONS,

  // ── D&H/CP YARDMASTER AGREEMENT (eff. April 15, 2007) ────────────────────
  'D&H/CP Yardmaster Agreement (eff. April 15, 2007)': [
    'Article 1 — Definitions / Scope Violation',
    'Article 2 — Yardmaster Classification Violation',
    'Article 3 — Basic Day / Overtime Violation',
    'Article 4 — Bulletins / Awards Violation',
    'Article 5 — Seniority Violation',
    'Article 6 — Displacement / Exercise of Seniority Denied',
    'Article 7 — Extra / Unassigned Yardmaster Violation',
    'Article 8 — Leave of Absence Violation',
    'Article 9 — Vacation Violation',
    'Article 10 — Holidays Violation',
    'Article 11 — Rest Days Violation',
    'Article 12 — Personal Leave Violation',
    'Article 13 — Sick Leave Violation',
    'Article 14 — Bereavement Violation',
    'Article 15 — Jury Duty Violation',
    'Article 16 — Military Service Violation',
    'Article 17 — Programming Violation',
    'Article 18 — Claims and Grievances — Procedure Violation',
    'Article 19 — Discipline / Appeals — Improper Procedure',
    'Article 20 — Non-Discrimination Violation',
    'Article 21 — Rate of Pay Violation',
    'Article 22 — Training Violation',
    'Article 23 — Positions Abolished — Improper Handling',
    'Article 24 — Starting Time Violation',
    'Appendix 9 — Goalsharing Program Violation',
  ],

  // ── CONRAIL MECHANICAL SUPERVISORS AGREEMENT (eff. May 1, 1994) ──────────
  'Conrail Mechanical Supervisors Agreement (eff. May 1, 1994)': [   // section 522 (2026-09-30): numbered to the 1994 schedule (src/data/conrailMsCatalog.js)
    'Scope — Jurisdiction Violation',
    'Rule 1 — Promotion Violation',
    'Rule 2 — Seniority Date Dispute',
    'Rule 2 — Seniority Roster Error or Omission',
    'Rule 3 — Bulletin / Award Violation',
    'Rule 3 — Displacement / Exercise of Seniority Denied',
    'Rule 4 — Hours of Service Violation',
    'Rule 5 — Basis of Pay / Overtime Violation',
    'Rule 6 — Rest Days / Calls Violation',
    'Rule 6 — Holiday Pay Violation',
    'Rule 7 — Temporary Assignment Violation',
    'Rule 8 — Traveling Time and Expenses Violation',
    'Rule 9 — Reduction of Forces / Position Abolished — Improper Handling',
    'Rule 10 — Transfer Violation',
    'Rule 11 — Committeemen Violation',
    'Rule 12 — Leave of Absence Violation',
    'Rule 13 — Vacation Violation',
    'Rule 14 — Claims and Grievances — Procedure Violation',
    'Rule 15 — Discipline / Appeals — Improper Procedure',
    'Rule 16 — Sick Allowance Violation',
    'Rule 17 — Personal Leave Violation',
    'Rule 18 — Court or Jury Duty Violation',
    'Rule 19 — Bereavement Leave Violation',
    'Rule 20 — Wreckmaster Violation',
    'Rule 21 — Board of Doctors Violation',
    'Rule 22 — Incapacitated Supervisors Violation',
    'Rule 25 — Automobile Allowance Violation',
    'Rule 26 — Termination of Seniority Violation',
    'Rule 28 — Non-Discrimination Violation',
    'Appendix A — Rate of Pay Violation',
  ],

  // ── BN YARDMASTER AGREEMENT (GO-341 / BNSF) ──────────────────────────────
  'BN Yardmaster Agreement': [
    'Article I — Scope Violation',
    'Section II — GREB Rest Days Violation',
    'Section III — Seniority Consolidation Violation',
    'Section IV — Temporary Transfer / Borrow Out Violation',
    'Section V — Dues Maintenance Violation',
    'Rule 4(B) — GREB Pay Guarantee Violation',
    'Rule of Appointment (ROA) — Violation',
    'General Wage Increase — Rate of Pay Violation',
    'Vacation — New Hire Entitlement Violation',
    'Sick Leave / FMLA Substitution Violation',
    'Discipline — Procedure Violation',
    'Claims and Grievances — Procedure Violation',
    'Seniority — Roster Error or Omission',
    'Bulletin / Award Violation',
    'Displacement / Exercise of Seniority Denied',
    'Position Abolished — Improper Handling',
    'Non-Discrimination Violation',
  ],

};

export const AGREEMENTS = [
  {
    id: 1,
    committee: 'GO-342',
    carrier: 'Amtrak / NRPC',
    railroadKey: 'Amtrak / NRPC',
    title: 'Amtrak Yardmaster Agreement',
    effective: 'January 1, 1983',
    amended: 'November 1, 2000',
    status: 'active',
    documents: 47,
    description: 'Covers all Amtrak yardmasters and assistant yardmasters on the Northeast Corridor and Midwest.',
    locations: [
      'NY-PSCC (Penn Station Central Control)',
    'SSY-West End (Sunnyside Yard — West End)',
    'SSY-East End (Sunnyside Yard — East End)',
      'Ivy City / Washington Terminal — Washington, DC',
      'Southampton Street Yard — Boston, MA',
      'Chi-Glass House (Chicago Coach Yard)',
      'Chi-14th St North (Chicago)',
      'Chi-14th St South (Chicago)',
      '30th Street Station (Race Street) — Philadelphia, PA',
      'Albany / Rensselaer — Rensselaer, NY',
    ],
  },
  {
    id: 2,
    committee: 'GO-342',
    carrier: 'CSX Transportation (Former Conrail Northern Lines)',
    railroadKey: 'CSX Transportation — Former Conrail (Northern Lines)',
    title: 'CSX Yardmaster System Agreement',
    effective: 'July 1, 2002',
    amended: null,
    status: 'active',
    documents: 18,
    description: 'GO-342 authority confined to former Conrail Northern Lines territory (Regions 23–27).',
    locations: [
      // Region 23 — NJ/NY Metro (Active)
      'Oak Point, NY',
      'South Kearney, NJ',
      'Elizabeth, NJ',
      'Hunts Point, NY',
      // Region 23 — NJ/NY Metro (Abolished)
      'North Bergen, NJ [ABOLISHED Jan 11, 2024 → South Kearny Desk]',
      'Manville, NJ [ABOLISHED Oct 28, 2023 → South Kearny Desk]',
      // Region 24 — Albany/Buffalo (Active)
      'Albany (Selkirk), NY',
      'Buffalo (Frontier), NY',
      'Niagara Falls, NY',
      'Rochester, NY',
      'Seneca, NY',
      'Syracuse (DeWitt), NY',
      'Abbot Road Yard, NY',
      'Burrows Lot Yard, NY',
      'Kenmore, NY',
      'Stock Yard, NY',
      'Watertown, NY',
      'West Albany, NY',
      // Region 25 — Boston/New England (Active)
      'Framingham, MA',
      'West Springfield, MA',
      'Worcester, MA',
      'Cedar Hill, CT',
      'Hartford, CT',
      'New Haven, CT',
      // Region 25 — Boston/New England (Abolished)
      'Boston (Beacon Park), MA [ABOLISHED Sep 15, 2017 → Worcester Desk]',
      'Readville, MA [ABOLISHED May 22, 2020 → Framingham Desk]',
      // Region 26 — Cleveland/Erie (Active)
      'Cleveland (Collinwood), OH',
      'Marcy Yard, OH',
      // Region 26 — Cleveland/Erie (Abolished)
      'Erie, PA [ABOLISHED Nov 2, 2018 → Collinwood Desk]',
      'Van Site, OH [ABOLISHED Mar 14, 2021 → Collinwood Desk]',
      'Cleveland/Parma, OH [ABOLISHED Aug 19, 2022 → Collinwood Desk]',
      // Region 27 — Ashtabula (Active)
      'Painesville, OH',
      // Region 27 — Ashtabula (Abolished)
      'Ashtabula, OH [ABOLISHED Jun 3, 2019 → Collinwood Desk]',
    ],
  },
  {
    id: 3,
    craft: 'Yardmaster',
    committee: 'GO-342',
    carrier: 'Consolidated Rail Corporation (Conrail)',
    railroadKey: 'Consolidated Rail Corporation (Conrail)',
    title: 'Conrail Yardmaster Agreement',
    effective: 'July 1, 1978',
    amended: null,
    status: 'active',
    documents: 1,
    description: 'Base agreement sourced: Railroad Yardmasters of America, eff. July 1, 1978 (data/agreements/conrail-rya-7-1-78.pdf, 127pp). Rules 1-9 catalogued — titles scan-verified, body OCR-derived pending line-verification. National/NCCC + IMOU modifications pending.',
    locations: [
      'Oak Island Hump',
      'Linden',
      'Manville',
      'Metuchen',
      'Greenville',
      'North Bergen',
      'Oak Island-Transfer'
    ],
  },
  {
    id: 4,
    committee: 'GO-342',
    carrier: 'Consolidated Rail Corporation / URSA',
    railroadKey: 'Consolidated Rail Corporation (Conrail)',
    craft: 'Mechanical Supervisor',
    title: 'Conrail Mechanical Supervisors Agreement',
    effective: 'May 1, 1994',
    amended: null,
    status: 'sourced',
    documents: 2,
    description: 'Covers Foremen in Maintenance of Equipment and Mechanical Foremen in Maintenance of Way. Modification rounds sourced and filed (1994 + 2003 CSAO). Base rule schedule (Rules and Rates of Pay as of May 1, 1994) catalogued 2026-09-30 with the 1995-round Article VI modifications layered in; text machine-extracted from the scan, pending transcription verification.',
    locations: [
      'Morrisville Shop — Morrisville, PA',
      'Columbus Shop — Columbus, OH',
],
  },
  {
    id: 5,
    committee: 'GO-342',
    carrier: 'Delaware & Hudson / Canadian Pacific',
    railroadKey: 'Delaware & Hudson / Canadian Pacific',
    title: 'D&H/CP Yardmaster Agreement',
    effective: 'April 15, 2007',
    amended: null,
    status: 'active',
    documents: 12,
    description: 'Revised April 15, 2007. Three prior rights divisions: Pennsylvania, Binghamton/Buffalo/Mohawk/Mechanicville, Capital District.',
    locations: [
      'Mechanicville Yard — Mechanicville, NY',
      'Binghamton Yard — Binghamton, NY',
      'Oneida — Oneida, NY',
      'Sunbury — Sunbury, PA',
      'Wilkes-Barre — Wilkes-Barre, PA',
      'Albany — Albany, NY',
      'Buffalo — Buffalo, NY',
    ],
  },
  {
    id: 6,
    committee: 'GO-341',
    carrier: 'Burlington Northern / BNSF',
    railroadKey: 'Burlington Northern / BNSF',
    title: 'BN Yardmaster Agreement',
    effective: 'RYA national rounds 1982-2025',
    amended: null,
    status: 'sourced',
    documents: 7,
    description: 'BASE SCHEDULE IN HAND as of 2026-08-23. The agreement Article XI of the 1994 memorandum calls the "Blue Book" — effective July 1, 1982 — is one instrument with the document now filed at data\\agreements\\BN_YM_Schedule_1997-05-10.pdf: its own Rule 31 states it was originally effective on the Burlington Northern Inc. merger of March 3, 1970, amended effective July 1, 1982, and amended and updated a second time effective May 10, 1997. Signed at Fort Worth 30 June 1997 by W.J. Cobean, General Chairman UTU Yardmasters Department. 31 rules, Definitions, Appendices A-J and a Side Letter on Rule 22, with a clean embedded text layer at data\\agreements\\BN_YM_Schedule_1997-05-10.source.txt — not an OCR transcript. Rule 6 carries six seniority districts including FRISCO, which confirms Article XI\'s carve-out preserving the cancelled 1945 StLSF agreement\'s seniority provisions. Wage/economic rounds also filed (RYA national 1982 A-10809; 1994 memo; Document B 2011/2017 A-13828/2022 A-14005; 2025 tentative; BNSF-SMART-TD 2025). Violation matrix currently built from the 1994 memorandum only (75 types, 19 remedy caps). RULE CATALOGUE BUILT 2026-09-14 (src\\data\\bnCatalog.js, rules 1-31 verbatim from the schedule\'s own text layer, proved by scripts\\test-bn-catalog.mjs); the picker and the resolver serve it as the 1997 edition.',
    locations: [],
  },
  {
    id: 7,
    committee: 'GO-247',
    carrier: 'CSX Transportation (Former B&O / B&I / CSXI)',
    railroadKey: 'CSX Transportation — Former B&O / B&I / CSXI',
    title: 'CSX Yardmaster System Agreement — B&O/B&I/CSXI Territory',
    effective: 'July 1, 2002',
    amended: null,
    status: 'active',
    documents: 0,
    description: 'GO-247 jurisdiction covering former B&O, B&I, and CSXI territory.',
    locations: [
      'Philadelphia, PA','Baltimore, MD','Brunswick, MD','Wilmington, DE','Jessup, MD','Washington, DC','York, PA',
      'Cumberland, MD','Hagerstown, MD','Martinsburg, WV','Elkins, WV','Keyser, WV',
      'Grafton, WV','Parkersburg, WV','Clarksburg, WV','Fairmont, WV',
      'Brooklyn Junction, WV','Holloway, OH','Lorain, OH','Wheeling Terminal, WV',
      'Pittsburgh, PA','Neville Island, PA','Aliquippa, PA','Connellsville, PA','Johnstown, PA','Somerset, PA',
      'New Castle, PA','Akron, OH','Cleveland/Parma, OH','Willard, OH','DeForrest Jct./Goodman, OH','Painesville, OH','Youngstown District, OH',
      'Lima, OH','Hamilton/Middletown, OH','Indianapolis, IN','South Anderson, IN','Avon, IN','Decatur, IL','Dayton, OH','South Dayton, OH',
      'Cincinnati, OH','Queensgate, OH','Mansfield, OH','Newark, OH','Zanesville, OH','Chillicothe, OH',
      'East Saint Louis, IL','Danville, IL','Flora, IL','North Vernon, IN','Washington, IN',
      'Garrett, IN','Ft. Wayne, IN','Chicago, IL',
      'Buffalo, NY','Butler, PA','East Salamanca, NY','Punxsutawney, PA','Rochester, NY',
    ],
  },
  {
    id: 8,
    committee: 'GO-347',
    carrier: 'CSX Transportation (Former C&O / L&N / Monon / CCR / NT)',
    railroadKey: 'CSX Transportation — Former C&O / L&N / Monon / CCR / NT',
    title: 'CSX Yardmaster System Agreement — C&O/LN/Monon Territory',
    effective: 'July 1, 2002',
    amended: null,
    status: 'active',
    documents: 0,
    description: 'GO-347 jurisdiction covering former C&O, L&N, Monon, CCR, and NT territory.',
    locations: [
      'Saginaw, MI','Plymouth, MI','Dearborn, MI','Grand Rapids, MI','Port Huron, MI','Toledo, OH','Fostoria, OH','Flint, MI','Ludington, MI','Muskegon, MI',
      'Columbus, OH','Marion, OH','Huntington, WV','Peach Creek, WV','Hinton, WV','Elk Run, WV','Charleston, WV','St. Albans, WV','Quinnimont, WV',
      'Newport News, VA','Richmond, VA (Fulton Yard)','Lynchburg, VA','Gladstone, VA','Covington, VA','Handley, WV',
      'Russell, KY','Shelby, KY','Martin, KY','Danville, WV','Paintsville, KY','Ashland, KY','Peru, IN',
      'Richmond, VA (Acca Yard)',
      'Lafayette, IN','Evansville, IN','Atkinson, KY','Louisville, KY','Bloomington, IN',
      'Corbin, KY','Knoxville, TN','Lexington, KY','Ravenna, KY','Loyall, KY','Hazard, KY','Etowah, TN','Erwin, TN','Kingsport, TN','Chattanooga, TN','Spartanburg, SC','Forrest City, NC','Dante, VA',
      'Nashville, TN','Bowling Green, KY','Bruceton, TN','Memphis, TN',
      'Birmingham, AL','Gadsden, AL','Decatur, AL','Montgomery, AL',
      'Mobile, AL','New Orleans, LA','Pensacola, FL',
    ],
  },
  {
    id: 9,
    committee: 'GO-SCL',
    carrier: 'CSX Transportation (Former Seaboard Coast Line)',
    railroadKey: 'CSX Transportation — Former Seaboard Coast Line (SCL)',
    title: 'CSX Yardmaster System Agreement — SCL Territory',
    effective: 'July 1, 2002',
    amended: null,
    status: 'active',
    documents: 0,
    description: 'GO-SCL jurisdiction covering former Seaboard Coast Line territory.',
    locations: [
      'Rocky Mount, NC','Charlotte, NC','Hamlet, NC','Wilmington, NC','Petersburg, VA','Monroe, NC',
      'Columbia, SC','Charleston, SC','Florence, SC','Greenwood, SC','Augusta, GA',
      'Waycross, GA','Atlanta, GA','Savannah, GA','Thomasville, GA',
      'Jacksonville, FL (Moncrief)',
      'Tampa, FL','Rockport, FL','Winston, FL','Hialeah, FL','Lakeland, FL',
    ],
  },
  {
    id: 10, committee: 'GO-340', carrier: 'Long Island Rail Road (LIRR)', railroadKey: 'Long Island Rail Road (LIRR)',
    title: 'LIRR Yardmaster Agreement', effective: 'TBD', amended: null, status: 'pending', documents: 0,
    description: 'Pending sourcing.', locations: [],
  },
  {
    id: 11, committee: 'GO-340', carrier: 'NJ Transit (NJT)', railroadKey: 'NJ Transit (NJT)',
    title: 'NJT Yardmaster Agreement', effective: 'TBD', amended: null, status: 'pending', documents: 0,
    description: 'Pending sourcing.', locations: [],
  },
  {
    id: 12, committee: 'GO-348', carrier: 'Norfolk & Western / Norfolk Southern', railroadKey: 'Norfolk & Western / Norfolk Southern',
    title: 'NW Yardmaster Agreement', effective: 'TBD', amended: null, status: 'pending', documents: 0,
    description: 'Pending sourcing.', locations: [],
  },
  {
    // 2026-10-16: GO-256 split. id 13 keeps the Soo Line schedule of rules it already carried in the library (seeded
    // 09-28 from the 2005-11-01 PDF); Metra is its own agreement, id 18.
    id: 13, committee: 'GO-256', carrier: 'Soo Line Railroad (CPKC)', railroadKey: 'Soo Line Railroad (CPKC)',
    title: 'Soo Line Yardmaster Schedule of Rules (eff. Nov 1, 2005)', effective: 'November 1, 2005', amended: null, status: 'active', documents: 0,
    description: 'Soo Line Railroad (CPKC) and UTU Yardmasters, schedule of rules effective November 1, 2005.', locations: [],
  },
  {
    id: 14, committee: 'GO-343', carrier: 'Union Pacific / Missouri Pacific / CNW', railroadKey: 'Union Pacific / Missouri Pacific / CNW',
    title: 'UP/MP/CNW Yardmaster Agreement', effective: 'TBD', amended: null, status: 'pending', documents: 0,
    description: 'Pending sourcing.', locations: [],
  },
  {
    id: 15, committee: 'GO-346', carrier: 'Southern Railway / Norfolk Southern', railroadKey: 'Southern Railway / Norfolk Southern',
    title: 'Southern Railway Yardmaster Agreement', effective: 'Revised as of April 26, 2000', amended: null, status: 'pending', documents: 1,   // NS_YM_2000 (2026-10-28)
    description: 'Norfolk Southern Railway Company and Yardmasters (UTU Yardmaster Department), revised as of April 26, 2000; territory: NS Railway, Central of Georgia, CNO&TP, Alabama Great Southern, New Orleans Terminal, Georgia Southern & Florida, Nickel Plate, Wabash, Pennsylvania, Wheeling & Lake Erie, Virginian. OCR text on file; not yet confirmed by the General Chairperson.', locations: [],
  },
  {
    id: 16, committee: 'GO-GTW', carrier: 'Grand Trunk Western / CN', railroadKey: 'Grand Trunk Western / CN',
    title: 'GTW Yardmaster Agreement', effective: 'TBD', amended: null, status: 'pending', documents: 0,
    description: 'Pending sourcing.', locations: [],
  },
  {
    id: 18, committee: 'GO-256', carrier: 'Metra (Northeast Illinois Regional Commuter Railroad)', railroadKey: 'Metra (Northeast Illinois Regional Commuter Railroad)',
    title: 'Metra (NIRC) Yardmaster Agreement (eff. Jan 1, 1984; rev. Sep 1, 2010)', effective: 'January 1, 1984', amended: 'September 1, 2010', status: 'active', documents: 0,
    description: 'Northeast Illinois Regional Commuter Railroad Corp. (Metra) and UTU Yardmasters, effective January 1, 1984, revised to September 1, 2010 (FOIA response 26-FOIA-731).', locations: [],
  },
  {
    id: 17, committee: 'GO-DMIR', carrier: 'Duluth Missabe & Iron Range / CN', railroadKey: 'Duluth Missabe & Iron Range / CN',
    title: 'DMIR Yardmaster Agreement', effective: 'TBD', amended: null, status: 'pending', documents: 0,
    description: 'Pending sourcing.', locations: [],
  },
];

// ── REPRESENTATIVE REFERENCE GUIDES ────────────────────────────────────────
export const REFERENCE_GUIDES = [
  {
    id: 'rule36-prelim-meeting',
    title: 'Amtrak Rule 36 — The Intent to Discipline Meeting',
    subtitle: 'A Complete Guide for Local Chairpersons and General Chairpersons',
    agreement: 'Amtrak Yardmaster Agreement (amended 2000, Article VII)',
    category: 'Discipline',
    access: 'lc_gc',
    description: 'Explains the purpose and strategic use of the Rule 36 intent meeting (not the hearing, and not a prehearing meeting) — what it is, what to demand, how to use it to build the record, and what happens if management fails to appear.',
    topics: [
      'What the intent meeting is actually for',
      'How to demand evidence and witnesses at the meeting',
      'Whether to request access to carrier witnesses — and why the denial matters',
      'Complete LC checklist: before, during, and after the meeting',
      'Automatic withdrawal when management does not appear',
    ],
    // The guide itself is written and under git at the path below (PD-1: files first).
    // pdfUrl stays null until a PDF is attached; the card reads docPath when it is.
    pdfUrl: null,
    docPath: 'data/guides/YCP_GUIDE_Amtrak_Rule36_Intent_Meeting.md',
    lastUpdated: 'July 13, 2026',
    author: 'YCP Platform',
  },
];

export const AGREEMENT_LOOKUP = {
  'GO-342': {
    'Yardmaster': {
      'Amtrak / NRPC': 'Amtrak Yardmaster Agreement (eff. Jan 1, 1983; Labor Relations Printout, Nov 1, 2000)',
      'CSX Transportation — Former Conrail (Northern Lines)': 'CSX Yardmaster System Agreement (eff. July 1, 2002)',
      'Delaware & Hudson / Canadian Pacific': 'D&H/CP Yardmaster Agreement (eff. April 15, 2007)',
      'Consolidated Rail Corporation (Conrail)': 'Conrail Yardmaster Agreement (eff. 1978)',
    },
    'Mechanical Supervisor': { 'Consolidated Rail Corporation (Conrail)': 'Conrail Mechanical Supervisors Agreement (eff. May 1, 1994)' }
  },
  'GO-341': { 'Yardmaster': { 'Burlington Northern / BNSF': 'BN Yardmaster Agreement' } },
  'GO-340': { 'Yardmaster': { 'Long Island Rail Road (LIRR)': 'LIRR Yardmaster Agreement', 'NJ Transit (NJT)': 'NJT Yardmaster Agreement' } },
  'GO-247': { 'Yardmaster': { 'CSX Transportation — Former B&O / B&I / CSXI': 'CSX Yardmaster System Agreement (eff. July 1, 2002) — B&O/B&I/CSXI Territory' } },
  'GO-347': { 'Yardmaster': { 'CSX Transportation — Former C&O / L&N / Monon / CCR / NT': 'CSX Yardmaster System Agreement (eff. July 1, 2002) — C&O/LN/Monon Territory' } },
  'GO-SCL': { 'Yardmaster': { 'CSX Transportation — Former Seaboard Coast Line (SCL)': 'CSX Yardmaster System Agreement (eff. July 1, 2002) — SCL Territory' } },
  'GO-348': { 'Yardmaster': { 'Norfolk & Western / Norfolk Southern': 'NW Yardmaster Agreement' } },
  'GO-256': { 'Yardmaster': {
    'Soo Line Railroad (CPKC)': 'Soo Line Yardmaster Schedule of Rules (eff. Nov 1, 2005)',
    'Metra (Northeast Illinois Regional Commuter Railroad)': 'Metra (NIRC) Yardmaster Agreement (eff. Jan 1, 1984; rev. Sep 1, 2010)',
  } },
  'GO-343': { 'Yardmaster': { 'Union Pacific / Missouri Pacific / CNW': 'UP/MP/CNW Yardmaster Agreement' } },
  'GO-346': { 'Yardmaster': { 'Southern Railway / Norfolk Southern': 'Southern Railway Yardmaster Agreement' } },
  'GO-GTW': { 'Yardmaster': { 'Grand Trunk Western / CN': 'GTW Yardmaster Agreement' } },
  'GO-DMIR': { 'Yardmaster': { 'Duluth Missabe & Iron Range / CN': 'DMIR Yardmaster Agreement' } },
};

// ── GC ROSTER LINKAGE (G7.1, 2026-09-14, task list section 224; ruling 128) ──────────────────
// The roster as given on 29 July named three things the platform data did not carry. What each
// became, on the record:
//   GO-PRT              WITHDRAWN by Bob in writing 2026-08-24 - PRT is an Amtrak agreement, not a
//                       railroad. It appears in no lookup above and must not be added.
//   GO-342 Single System  The CSX Yardmaster System Agreement of 2002-07-01 is ONE instrument whose
//                       territories are custodied by four committees (ids 2, 7, 8, 9 above). It is a
//                       shared AGREEMENT, never a shared office: each committee keeps sole authority
//                       over its own territory and every custodian act is attributed to one General
//                       Chairperson (data\YCP_TASK_LIST.md, 2026-08-23 SS3 - "putting that sharing
//                       on the OFFICE ... would make 'was he authorized on the day he signed'
//                       unanswerable in a 2029 arbitration").
//   USCA-TCU            Given on the roster as sharing an agreement with GO-343 (UP/MP/CNW). That is
//                       the whole of what is on record. Carried here as UNCONFIRMED so the page can
//                       say so; it maps no railroad and is in no lookup. When a chairman confirms
//                       the relationship, `confirmed` becomes the date and the source, in this file.
export const SHARED_AGREEMENTS = [
  {
    instrument: 'CSX Yardmaster System Agreement (eff. July 1, 2002)',
    agreementIds: [2, 7, 8, 9],
    custodians: { 2: 'GO-342', 7: 'GO-247', 8: 'GO-347', 9: 'GO-SCL' },
    kind: 'single_system',
    confirmed: '2026-07-29 roster as given; structure ruled 2026-08-23 (shared agreement, not shared office)',
  },
  {
    instrument: 'UP/MP/CNW Yardmaster Agreement',
    agreementIds: [14],
    custodians: { 14: 'GO-343' },
    kind: 'shared_unconfirmed',
    party: 'USCA-TCU',
    confirmed: null,
  },
];

export const WITHDRAWN_COMMITTEES = {
  'GO-PRT': 'Withdrawn by Bob in writing 2026-08-24: PRT is an Amtrak agreement, not a railroad.',
};

// TERRITORY LOCATIONS (task list section 807): home terminals for crafts whose agreements are not yet on file - the T&E
// pack, generated by scripts\gen-te-content.py from data\YCP_TE_LOCATION_SEAT_MAP.csv. railroadKey -> locations.
export const TERRITORY_LOCATIONS = {
  "AWP (A&WP-W RY of AL)": [
    "Montgomery, AL (Local 762)"
  ],
  "Alaska Railroad": [
    "Anchorage, AK (Local 1626)"
  ],
  "Alton & Southern RY": [
    "East St. Louis, IL (Local 1929)"
  ],
  "Amtrak — Amtrak (GO-663)": [
    "Attleboro, MA (Local 679)",
    "Boston, MA (Local 1462)",
    "Boston, MA (Local 262)",
    "Hartford, CT (Local 277)",
    "New Haven, CT (Local 1361)",
    "New Haven, CT (Local 328)",
    "New London, CT (Local 1672)"
  ],
  "Amtrak — Former Conrail–pc–ple (GO-769)": [
    "Belen, NM (Local 1687)",
    "Boston, MA (Local 898)",
    "Carbondale, IL (Local 1525)",
    "Chicago, IL (Local 168)",
    "Edmonston, MD (Local 1470)",
    "Elizabeth, NJ (Local 1445)",
    "Fort Lauderdale, FL (Local 33)",
    "Harrisburg, PA (Local 816)",
    "Jacksonville, FL (Local 30)",
    "Los Angeles, CA (Local 84)",
    "Milwaukee, WI (Local 281)",
    "New York, NY (Local 1370)",
    "Newark, NJ (Local 1447)",
    "Philadelphia, PA (Local 1375)",
    "Philadelphia, PA (Local 300)",
    "Philadelphia, PA (Local 838)",
    "Rensselaer, NY (Local 95)",
    "Salt Lake City, UT (Local 166)",
    "San Antonio, TX (Local 857)",
    "San Jose, CA (Local 1732)",
    "Trenton, NJ (Local 1390)",
    "Vancouver, WA (Local 117)",
    "Washington, DC (Local 1522)",
    "Washington, DC (Local 1933)"
  ],
  "BNSF Railway — Former Alabama Tennessee & Northern (GO-001)": [
    "Alliance, NE (Local 934)",
    "Amarillo, TX (Local 1313)",
    "Amory, MS (Local 853)",
    "Aurora, IL (Local 171)",
    "Beardstown, IL (Local 196)",
    "Birmingham, AL (Local 847)",
    "Brookfield, MO (Local 185)",
    "Casper, WY (Local 1280)",
    "Chaffee, MO (Local 947)",
    "Chicago, IL (Local 620)",
    "Creston, IA (Local 199)",
    "Denver, CO (Local 202)",
    "Duluth, MN (Local 1175)",
    "Edgemont, SD (Local 375)",
    "Enid, OK (Local 1016)",
    "Everett, WA (Local 1713)",
    "Fargo, ND (Local 1137)",
    "Fort Worth, TX (Local 818)",
    "Galesburg, IL (Local 1423)",
    "Galesburg, IL (Local 195)",
    "Gillette, WY (Local 465)",
    "Glasgow, MT (Local 1840)",
    "Glendive, MT (Local 486)",
    "Grand Forks, ND (Local 525)",
    "Great Falls, MT (Local 730)",
    "Greybull, WY (Local 1279)",
    "Hannibal, MO (Local 219)",
    "Havre, MT (Local 544)",
    "Kansas City, MO (Local 5)",
    "Klamath Falls, OR (Local 1841)",
    "La Crosse, WI (Local 311)",
    "Laurel, MT (Local 3)",
    "Lincoln, NE (Local 305)",
    "Mandan, ND (Local 1344)",
    "Mccook, NE (Local 626)",
    "Memphis, TN (Local 1420)",
    "Minneapolis, MN (Local 1000)",
    "Minot, ND (Local 1059)",
    "Oklahoma City, OK (Local 1042)",
    "Omaha, NE (Local 872)",
    "Pasco, WA (Local 977)",
    "Seattle, WA (Local 324)",
    "Seattle, WA (Local 845)",
    "Sheridan, WY (Local 951)",
    "Sherman, TX (Local 949)",
    "Sioux City, IA (Local 418)",
    "Spokane, WA (Local 426)",
    "Spokane, WA (Local 855)",
    "Springfield, MO (Local 303)",
    "St. Louis, MO (Local 1388)",
    "Sterling, CO (Local 1136)",
    "Superior, WI (Local 832)",
    "Tacoma, WA (Local 556)",
    "Thayer, MO (Local 607)",
    "Tulsa, OK (Local 1289)",
    "Tulsa, OK (Local 894)",
    "Vancouver, WA (Local 1238)",
    "Whitefish, MT (Local 891)",
    "Wichita Falls, TX (Local 940)",
    "Willmar, MN (Local 1177)"
  ],
  "BNSF Railway — Former At&sf–coast (GO-017)": [
    "Bakersfield, CA (Local 1581)",
    "Barstow, CA (Local 1694)",
    "Fresno, CA (Local 1252)",
    "Glendale, AZ (Local 1081)",
    "Maywood, CA (Local 1544)",
    "Needles, CA (Local 771)",
    "Richmond, CA (Local 1241)",
    "Richmond, CA (Local 1730)",
    "San Bernardino, CA (Local 811)",
    "Winslow, AZ (Local 113)"
  ],
  "BNSF Railway — Former At&sf–coast (GO-020)": [
    "Amarillo, TX (Local 1313)",
    "Arkansas City, KS (Local 464)",
    "Bakersfield, CA (Local 1581)",
    "Belen, NM (Local 1687)",
    "Chicago, IL (Local 1494)",
    "Clovis, NM (Local 1168)",
    "El Paso, TX (Local 1571)",
    "Glendale, AZ (Local 1081)",
    "La Junta, CO (Local 945)",
    "Los Angeles, CA (Local 1674)",
    "Montebello, CA (Local 1701)",
    "Needles, CA (Local 771)",
    "Newton, KS (Local 477)",
    "Niota, IL (Local 445)",
    "Perris, CA (Local 1707)",
    "Pueblo, CO (Local 204)",
    "Richmond, CA (Local 1241)",
    "San Bernardino, CA (Local 811)",
    "Slaton, TX (Local 9)",
    "Wellington, KS (Local 794)",
    "Winslow, AZ (Local 113)"
  ],
  "BNSF Railway — Former At&sf–proper (GO-009)": [
    "Amarillo, TX (Local 1313)",
    "Arkansas City, KS (Local 464)",
    "Belen, NM (Local 1687)",
    "Chicago, IL (Local 1494)",
    "Clovis, NM (Local 1168)",
    "El Paso, TX (Local 1571)",
    "Grand Junction, CO (Local 500)",
    "Kahoka, MO (Local 643)",
    "Kansas City, KS (Local 1532)",
    "Kansas City, KS (Local 94)",
    "La Junta, CO (Local 945)",
    "Newton, KS (Local 477)",
    "Niota, IL (Local 445)",
    "Oklahoma City, OK (Local 1188)",
    "Slaton, TX (Local 9)",
    "Wellington, KS (Local 794)"
  ],
  "BNSF Railway — Former At&sf–western (GO-393)": [
    "Brownwood, TX (Local 1593)",
    "Cleburne, TX (Local 564)",
    "Gainesville, TX (Local 513)",
    "Galveston, TX (Local 773)",
    "Silsbee, TX (Local 1957)",
    "Temple, TX (Local 331)"
  ],
  "BNSF Railway — Former Great Northern (GO-386)": [
    "Aberdeen, SD (Local 233)",
    "Cheyenne, WY (Local 446)",
    "Denver, CO (Local 202)",
    "Fargo, ND (Local 1137)",
    "Glasgow, MT (Local 1840)",
    "Grand Forks, ND (Local 525)",
    "Great Falls, MT (Local 730)",
    "Havre, MT (Local 544)",
    "Klamath Falls, OR (Local 1841)",
    "Minot, ND (Local 1059)",
    "Seattle, WA (Local 324)",
    "Sioux City, IA (Local 418)",
    "Spokane, WA (Local 426)",
    "Superior, WI (Local 832)",
    "Trinidad, CO (Local 201)",
    "Vancouver, WA (Local 1238)",
    "Whitefish, MT (Local 891)",
    "Willmar, MN (Local 1177)",
    "Wishram, WA (Local 1637)"
  ],
  "BNSF Railway — Former Joint Texas Division of CRI&P and FW&D (GO-JTD)": [
    "Teague, TX (Local 1092)"
  ],
  "Bessemer & LAKE ERIE RR": [
    "Albion, PA (Local 596)"
  ],
  "CSX Transportation — Allegheny & Eastern Railroad (GO-049)": [
    "Albany, NY (Local 212)",
    "Baltimore, MD (Local 610)",
    "Bangor, ME (Local 663)",
    "Boston, MA (Local 1473)",
    "Brunswick, MD (Local 631)",
    "Buffalo, NY (Local 1)",
    "Buffalo, NY (Local 1393)",
    "Chicago, IL (Local 1534)",
    "Cincinnati, OH (Local 14)",
    "Cleveland, OH (Local 284)",
    "Cleveland, OH (Local 378)",
    "Columbus, OH (Local 1397)",
    "Columbus, OH (Local 145)",
    "Connellsville, PA (Local 340)",
    "Cumberland, MD (Local 600)",
    "Danville, IL (Local 1358)",
    "Detroit, MI (Local 1760)",
    "East Syracuse, NY (Local 292)",
    "Evansville, IN (Local 904)",
    "Frankfort, IN (Local 744)",
    "Garrett, IN (Local 298)",
    "Grafton, WV (Local 605)",
    "Grand Rapids, MI (Local 313)",
    "Indianapolis, IN (Local 1518)",
    "Indianapolis, IN (Local 1548)",
    "Indianapolis, IN (Local 6)",
    "Lima, OH (Local 138)",
    "New Castle, PA (Local 1374)",
    "Newark, NJ (Local 1447)",
    "Parkersburg, WV (Local 1386)",
    "Philadelphia, PA (Local 1373)",
    "Pittsburgh, PA (Local 1379)",
    "Richmond, VA (Local 662)",
    "Salamanca, NY (Local 377)",
    "Syracuse, NY (Local 1007)",
    "Toledo, OH (Local 1816)",
    "Walbridge, OH (Local 1529)",
    "West Springfield, MA (Local 352)",
    "Wheeling, WV (Local 504)",
    "Willard, OH (Local 586)",
    "Wilmington, DE (Local 1378)"
  ],
  "CSX Transportation — Florida East Coast Ry (GO-851)": [
    "Abbeville, SC (Local 970)",
    "Atlanta, GA (Local 1261)",
    "Augusta, GA (Local 674)",
    "Birmingham, AL (Local 847)",
    "Charleston, SC (Local 407)",
    "Charlotte, NC (Local 1166)",
    "Fitzgerald, GA (Local 1790)",
    "Florence, SC (Local 942)",
    "Hamlet, NC (Local 1011)",
    "Jacksonville, FL (Local 903)",
    "Manchester, GA (Local 1598)",
    "Miami, FL (Local 1138)",
    "Montgomery, AL (Local 762)",
    "Raleigh, NC (Local 1129)",
    "Richmond, VA (Local 924)",
    "Rocky Mount, NC (Local 1106)",
    "Sanford, FL (Local 1504)",
    "Savannah, GA (Local 1031)",
    "Spencer, NC (Local 783)",
    "Tampa, FL (Local 1221)",
    "Waycross, GA (Local 998)",
    "Wildwood, FL (Local 1502)",
    "Wilmington, NC (Local 1105)"
  ],
  "CSX Transportation — Former C&O–P (GO-201)": [
    "Ashland, KY (Local 630)",
    "Handley, WV (Local 915)",
    "Hinton, WV (Local 118)",
    "Richmond, VA (Local 662)",
    "Russell, KY (Local 1377)"
  ],
  "CSX Transportation — Former L&n–proper (GO-513)": [
    "Atlanta, GA (Local 1033)",
    "Birmingham, AL (Local 847)",
    "Bruceton/Hollow, TN (Local 1308)",
    "Covington, KY (Local 1315)",
    "Erwin, TN (Local 1162)",
    "Etowah, TN (Local 1314)",
    "Evansville, IN (Local 904)",
    "Louisville, KY (Local 376)",
    "Mobile, AL (Local 598)",
    "Montgomery, AL (Local 762)",
    "Nashville, TN (Local 1346)",
    "Nashville, TN (Local 974)",
    "New Orleans, LA (Local 1066)",
    "Pensacola, FL (Local 1312)",
    "Ravenna, KY (Local 1316)"
  ],
  "Canadian National (grand Trunk Western RR) — Detroit & Toledo Shore Line (GO-217)": [
    "Chicago, IL (Local 1973)",
    "Toledo, OH (Local 1962)"
  ],
  "Canadian National (grand Trunk Western RR) — Former DT&I (GO-377)": [
    "Battle Creek, MI (Local 72)",
    "Pontiac, MI (Local 1709)",
    "Port Huron, MI (Local 1183)",
    "Trenton, MI (Local 1075)"
  ],
  "Canadian National (illinois Central)": [
    "Baton Rouge, LA (Local 1501)",
    "Carbondale, IL (Local 1525)",
    "Champaign-Urbana, IL (Local 432)",
    "Chicago, IL (Local 1299)",
    "Clinton, IL (Local 453)",
    "Jackson, TN (Local 339)",
    "Mccomb, MS (Local 427)",
    "Memphis, TN (Local 1557)",
    "Memphis, TN (Local 753)",
    "Meridian, MS (Local 584)",
    "Paducah, KY (Local 785)",
    "Waterloo, IA (Local 493)",
    "Wheeling, WV (Local 504)"
  ],
  "Canadian National (wisconsin Central)": [
    "Fond Du Lac, WI (Local 583)",
    "Green Bay, WI (Local 581)",
    "Joliet, IL (Local 740)",
    "Proctor, MN (Local 1292)",
    "Stevens Point, WI (Local 582)",
    "Virginia, MN (Local 1067)"
  ],
  "Canadian Pacific (SOO LINE)": [
    "Elkhart, IN (Local 194)",
    "Elmwood Park, IL (Local 1433)",
    "Enderlin, ND (Local 980)",
    "Grand Forks, ND (Local 525)",
    "Harvey, ND (Local 887)",
    "Marquette, MI (Local 886)",
    "Milwaukee, WI (Local 322)",
    "Minneapolis, MN (Local 911)",
    "Portage, WI (Local 590)",
    "Superior, WI (Local 832)"
  ],
  "Canadian Pacific (delaware & Hudson RY)": [
    "Watervliet, NY (Local 256)"
  ],
  "Cedar Rapids & IOWA CITY": [
    "Cedar Rapids, IA (Local 228)"
  ],
  "Central Railroad of Indiana": [
    "Sharonville, OH (Local 440)"
  ],
  "Chicago RAIL LINK": [
    "Chicago, IL (Local 1299)"
  ],
  "Colorado & Wyoming (former GO-306)": [
    "Pueblo, CO (Local 204)"
  ],
  "Columbia & Cowlitz RY": [
    "Centralia, WA (Local 1348)"
  ],
  "DES Moines Union RY": [
    "Des Moines, IA (Local 867)"
  ],
  "Dakota Minnesota & Eastern RR CORP": [
    "Waseca, MN (Local 64)"
  ],
  "Dallas Garland & Northeastern RR": [
    "Dallas, TX (Local 965)"
  ],
  "Delray Connecting RR": [
    "Lincoln Park, MI (Local 1438)"
  ],
  "EAST ERIE Commercial RR": [
    "Buffalo, NY (Local 1)"
  ],
  "GARY Railway (former Elgin Joliet & Eastern)": [
    "Gary, IN (Local 1383)"
  ],
  "IOWA Interstate RR": [
    "Rock Island, IL (Local 258)"
  ],
  "Illinois & Midland": [
    "Decatur, IL (Local 768)"
  ],
  "Indiana Harbor BELT RR — (GO-449)": [
    "Hammond, IN (Local 1381)",
    "Riverdale, IL (Local 1883)"
  ],
  "Indiana Harbor BELT RR — Formerly GCA 623 (GO-IHB)": [
    "Hammond, IN (Local 1381)"
  ],
  "Kansas CITY Southern Lines (KCS) - C,t,y,e,b,f, & O": [
    "Baton Rouge, LA (Local 1501)",
    "Beaumont, TX (Local 20)",
    "Chicago, IL (Local 1299)",
    "East St. Louis, IL (Local 1929)",
    "Greenville, TX (Local 878)",
    "Heavener, OK (Local 770)",
    "Jackson, MS (Local 1088)",
    "Kansas City, MO (Local 1780)",
    "Leesville, LA (Local 659)",
    "Meridian, MS (Local 584)",
    "Minden, LA (Local 1678)",
    "New Orleans, LA (Local 1066)",
    "Pittsburg, KS (Local 763)",
    "Shreveport, LA (Local 781)"
  ],
  "Kansas CITY Terminal RY": [
    "Kansas City, MO (Local 1780)"
  ],
  "LAKE Superior & Ishpeming RR": [
    "Marquette, MI (Local 886)"
  ],
  "LONG Island Railroad": [
    "Babylon, NY (Local 1831)",
    "Babylon, NY (Local 29)",
    "Babylon, NY (Local 645)",
    "Babylon, NY (Local 722)"
  ],
  "Longview Portland & Northern": [
    "Centralia, WA (Local 1348)"
  ],
  "Louisiana & Delta RR": [
    "Baton Rouge, LA (Local 1501)"
  ],
  "Louisiana & North WEST RR": [
    "Shreveport, LA (Local 781)"
  ],
  "Metra": [
    "Blue Island, IL (Local 653)",
    "Chicago, IL (Local 1290)",
    "Elgin, IL (Local 1258)",
    "Milwaukee, WI (Local 281)"
  ],
  "Minnesota Dakota & Western RY": [
    "Duluth, MN (Local 1175)"
  ],
  "NEW England Lines": [
    "Bangor, ME (Local 663)",
    "Boston, MA (Local 1473)",
    "Fitchburg, MA (Local 254)",
    "Greenfield, MA (Local 587)",
    "South Portland, ME (Local 1400)"
  ],
  "NEW Jersey Transit RAIL Operations INC": [
    "Newark, NJ (Local 60)"
  ],
  "NEW Orleans Public BELT RR": [
    "New Orleans, LA (Local 1066)"
  ],
  "Norfolk & Portsmouth BELT LINE": [
    "Portsmouth, VA (Local 854)"
  ],
  "Norfolk Southern Lines — Alabama Great Southern (GO-898)": [
    "Alexandria, VA (Local 769)",
    "Allentown, PA (Local 498)",
    "Altoona, PA (Local 309)",
    "Appalachia, VA (Local 1601)",
    "Asheville, NC (Local 782)",
    "Atlanta, GA (Local 1245)",
    "Atlanta, GA (Local 1261)",
    "Baltimore, MD (Local 610)",
    "Birmingham, AL (Local 622)",
    "Brownsville, PA (Local 1006)",
    "Charleston, SC (Local 407)",
    "Charlotte, NC (Local 1166)",
    "Chattanooga, TN (Local 338)",
    "Columbia, SC (Local 793)",
    "Conway, PA (Local 1418)",
    "Danville, KY (Local 573)",
    "Fairfield, AL (Local 1887)",
    "Greenville, SC (Local 931)",
    "Harrisburg, PA (Local 830)",
    "Jacksonville, FL (Local 903)",
    "Knoxville, TN (Local 1301)",
    "Knoxville, TN (Local 750)",
    "Louisville, KY (Local 1328)",
    "Ludlow, KY (Local 1190)",
    "Macon, GA (Local 1910)",
    "Meridian, MS (Local 584)",
    "Mobile, AL (Local 598)",
    "New Orleans, LA (Local 1066)",
    "Philadelphia, PA (Local 1375)",
    "Princeton, IN (Local 490)",
    "Raleigh, NC (Local 1129)",
    "Reading, PA (Local 386)",
    "Richmond, VA (Local 924)",
    "Selma, AL (Local 1053)",
    "Sheffield, AL (Local 772)",
    "Spencer, NC (Local 783)",
    "Valdosta, GA (Local 1263)",
    "Wilmington, DE (Local 1378)"
  ],
  "Norfolk Southern Lines — Former N&W–P & Vrgn (GO-680)": [
    "Appalachia, VA (Local 1601)",
    "Augusta, GA (Local 674)",
    "Bluefield, WV (Local 655)",
    "Columbus, GA (Local 941)",
    "Crewe, VA (Local 971)",
    "Macon, GA (Local 1910)",
    "Mullens, WV (Local 1172)",
    "Norfolk, VA (Local 48)",
    "Portsmouth, OH (Local 496)",
    "Portsmouth, VA (Local 854)",
    "Roanoke, VA (Local 363)",
    "Savannah, GA (Local 1031)",
    "Sharonville, OH (Local 440)"
  ],
  "Norfolk Southern Lines — Former N&w–nkp–w&le (GO-687)": [
    "Bellevue, OH (Local 225)",
    "Buffalo, NY (Local 1566)",
    "Chicago, IL (Local 1895)",
    "Cleveland, OH (Local 792)",
    "Conneaut, OH (Local 421)",
    "Conway, PA (Local 1418)",
    "Decatur, IL (Local 768)",
    "Elkhart, IN (Local 194)",
    "Fort Wayne, IN (Local 1202)",
    "Frankfort, IN (Local 744)",
    "Hornell, NY (Local 318)",
    "Jackson, MI (Local 278)",
    "Kankakee, IL (Local 1003)",
    "Kansas City, MO (Local 1216)",
    "Lincoln Park, MI (Local 1438)",
    "Moberly, MO (Local 226)",
    "Peru, IN (Local 206)",
    "St. Louis, MO (Local 1405)",
    "Toledo, OH (Local 1928)",
    "Toledo, OH (Local 2)",
    "Youngstown, OH (Local 1365)"
  ],
  "Northern Indiana Commuter": [
    "Michigan City, IN (Local 1526)"
  ],
  "PORT Authority Trans-hudson CORP (tower Group)": [
    "Jersey City, NJ (Local 1413)"
  ],
  "PORT Terminal RR Association": [
    "Houston, TX (Local 1904)"
  ],
  "PORT Terminal Railroad": [
    "Ft Worth, TX (Local 1974)"
  ],
  "Rapid CITY Pierre & Eastern Railroad": [
    "Huron, SD (Local 13)"
  ],
  "Southeastern Pennsylvania Transportation Authority": [
    "Philadelphia, PA (Local 61)"
  ],
  "Staten Island Railroad": [
    "Staten Island, NY (Local 1440)"
  ],
  "Tacoma Municipal BELT LINE": [
    "Tacoma, WA (Local 556)"
  ],
  "Tazewell & Peoria Railroad": [
    "Peoria, IL (Local 198)"
  ],
  "Terminal RR ASSN of ST Louis": [
    "Madison, IL (Local 469)"
  ],
  "Texas CITY Terminal RY": [
    "Galveston, TX (Local 773)"
  ],
  "Toledo Peoria & Western RR": [
    "Peoria, IL (Local 198)"
  ],
  "Union Pacific — Alameda Belt Line (GO-887)": [
    "Bakersfield, CA (Local 835)",
    "Dalhart, TX (Local 923)",
    "Dunsmuir, CA (Local 694)",
    "El Paso, TX (Local 18)",
    "Los Angeles, CA (Local 1422)",
    "Los Angeles, CA (Local 1770)",
    "Los Angeles, CA (Local 240)",
    "Oakland, CA (Local 100)",
    "Oakland, CA (Local 239)",
    "Phoenix, AZ (Local 1629)",
    "Roseville, CA (Local 1570)",
    "Sacramento, CA (Local 492)",
    "San Jose, CA (Local 31)",
    "Sparks, NV (Local 1043)",
    "Stockton, CA (Local 1201)",
    "Tucson, AZ (Local 1800)",
    "Tucson, AZ (Local 807)",
    "West Colton, CA (Local 1813)",
    "West Colton, CA (Local 1846)",
    "Wichita, KS (Local 1227)"
  ],
  "Union Pacific — Eastern District (GO-953)": [
    "Altoona, WI (Local 1293)",
    "Boone, IA (Local 329)",
    "Cedar Rapids, IA (Local 228)",
    "Centralia, WA (Local 1348)",
    "Cheyenne, WY (Local 446)",
    "Chicago, IL (Local 528)",
    "Clinton, IA (Local 316)",
    "Council Bluffs, IA (Local 646)",
    "Denver, CO (Local 202)",
    "Des Moines, IA (Local 867)",
    "Des Plaines, IL (Local 597)",
    "Eagle Grove, IA (Local 306)",
    "Eugene, OR (Local 471)",
    "Grand Junction, CO (Local 500)",
    "Green River, WY (Local 1857)",
    "Herington, KS (Local 506)",
    "La Grande, OR (Local 473)",
    "Las Vegas, NV (Local 1117)",
    "Marshalltown, IA (Local 17)",
    "Marysville, KS (Local 1503)",
    "Milford, UT (Local 1294)",
    "Milwaukee, WI (Local 322)",
    "Minneapolis, MN (Local 650)",
    "Morrill, NE (Local 257)",
    "Nampa, ID (Local 1058)",
    "North Platte, NE (Local 200)",
    "North Platte, NE (Local 286)",
    "North Platte, NE (Local 7)",
    "Northlake, IL (Local 577)",
    "Ogden, UT (Local 1554)",
    "Ogden, UT (Local 238)",
    "Omaha, NE (Local 367)",
    "Phillipsburg, KS (Local 44)",
    "Pocatello, ID (Local 265)",
    "Portland, OR (Local 1574)",
    "Pueblo, CO (Local 204)",
    "Rawlins, WY (Local 866)",
    "Salina, KS (Local 495)",
    "Salt Lake City, UT (Local 1366)",
    "Spokane, WA (Local 1505)",
    "St. Paul, MN (Local 1614)",
    "Vancouver, WA (Local 117)",
    "Wichita, KS (Local 1227)"
  ],
  "Union Pacific — Former HB&T (GO-577)": [
    "Beaumont, TX (Local 20)",
    "Dequincy, LA (Local 1458)",
    "Houston, TX (Local 1524)",
    "Houston, TX (Local 1892)",
    "Houston, TX (Local 293)",
    "Kingsville, TX (Local 1205)",
    "Lake Charles, LA (Local 1947)",
    "New Orleans, LA (Local 1337)",
    "New Orleans, LA (Local 1836)",
    "Palestine, TX (Local 524)",
    "Victoria, TX (Local 953)"
  ],
  "Union Pacific — Former Missouri Pacific–p (GO-569)": [
    "Atchison, KS (Local 774)",
    "Bloomington, IL (Local 234)",
    "Coffeyville, KS (Local 527)",
    "Dupo, IL (Local 1402)",
    "Jefferson City, MO (Local 933)",
    "Kansas City, KS (Local 1409)",
    "Kansas City, MO (Local 1216)",
    "Kansas City, MO (Local 1403)",
    "Monroe, LA (Local 1545)",
    "North Little Roc, AR (Local 656)",
    "North Little Rock, AR (Local 221)",
    "Osawatomie, KS (Local 533)",
    "Pine Bluff, AR (Local 462)",
    "Poplar Bluff, MO (Local 330)",
    "Salem, IL (Local 979)",
    "St. Louis, MO (Local 1823)",
    "Van Buren, AR (Local 507)",
    "Villa Grove, IL (Local 1083)",
    "West Memphis, AR (Local 950)"
  ],
  "Union Pacific — Former Texas & Pacific (GO-927)": [
    "Beaumont, TX (Local 20)",
    "Big Spring, TX (Local 823)",
    "Dallas, TX (Local 965)",
    "Del Rio, TX (Local 821)",
    "Dequeen, AR (Local 733)",
    "Ennis, TX (Local 569)",
    "Fort Worth, TX (Local 243)",
    "Houston, TX (Local 11)",
    "Houston, TX (Local 1892)",
    "Laredo, TX (Local 1670)",
    "Mart, TX (Local 937)",
    "Mineola, TX (Local 594)",
    "New Orleans, LA (Local 1337)",
    "Oklahoma City, OK (Local 1188)",
    "San Antonio, TX (Local 489)",
    "San Antonio, TX (Local 756)",
    "San Antonio, TX (Local 857)",
    "Shreveport, LA (Local 976)",
    "Smithville, TX (Local 508)",
    "Springfield, MO (Local 303)",
    "Tyler, TX (Local 439)"
  ],
  "White PASS & Yukon RR": [
    "Anchorage, AK (Local 1626)"
  ],
  "YORK Railway CO": [
    "Harrisburg, PA (Local 830)",
    "Reading, PA (Local 386)"
  ]
};
